import * as React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';

import { Button, buttonVariants } from 'components/ui/button';
import { cn } from 'lib/utils';

function CalendarDayButton({
    className,
    day,
    modifiers,
    ...props
}: React.ComponentProps<typeof DayButton>) {
    const defaultClassNames = getDefaultClassNames();
    const ref = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (modifiers.focused) {
            ref.current?.focus();
        }
    }, [modifiers.focused]);

    return (
        <Button
            ref={ref}
            variant="ghost"
            size="icon"
            data-day={day.date.toLocaleDateString()}
            data-selected-single={
                modifiers.selected &&
                !modifiers.range_start &&
                !modifiers.range_end &&
                !modifiers.range_middle
            }
            data-range-start={modifiers.range_start}
            data-range-end={modifiers.range_end}
            data-range-middle={modifiers.range_middle}
            data-today={modifiers.today}
            data-outside={modifiers.outside}
            data-disabled={modifiers.disabled}
            className={cn(
                'h-10 w-10 min-w-10 rounded-full p-0 text-sm font-normal text-foreground hover:bg-muted',
                'data-[outside=true]:text-muted-foreground',
                'data-[disabled=true]:pointer-events-none data-[disabled=true]:text-muted-foreground/35 data-[disabled=true]:hover:bg-transparent',
                'data-[today=true]:font-semibold data-[today=true]:underline data-[today=true]:underline-offset-4 data-[range-start=true]:no-underline data-[range-end=true]:no-underline',
                'data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-transparent data-[range-middle=true]:!text-foreground data-[range-middle=true]:hover:bg-transparent',
                'data-[range-start=true]:rounded-full data-[range-start=true]:bg-primary data-[range-start=true]:!text-white data-[range-start=true]:hover:bg-primary/90',
                'data-[range-end=true]:rounded-full data-[range-end=true]:bg-primary data-[range-end=true]:!text-white data-[range-end=true]:hover:bg-primary/90',
                'data-[selected-single=true]:rounded-full data-[selected-single=true]:bg-primary data-[selected-single=true]:!text-white data-[selected-single=true]:hover:bg-primary/90',
                defaultClassNames.day,
                className,
            )}
            {...props}
        />
    );
}

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'label',
    buttonVariant = 'ghost',
    formatters,
    components,
    ...props
}: React.ComponentProps<typeof DayPicker> & {
    buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
    const defaultClassNames = getDefaultClassNames();

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn('group/calendar p-3 text-foreground', className)}
            captionLayout={captionLayout}
            formatters={{
                formatMonthDropdown: (date) => date.toLocaleString('default', { month: 'short' }),
                ...formatters,
            }}
            classNames={{
                root: cn('relative w-fit', defaultClassNames.root),
                months: cn('relative flex flex-row gap-8', defaultClassNames.months),
                month: cn('flex flex-col gap-4', defaultClassNames.month),
                nav: cn(
                    'absolute inset-x-0 top-0 flex w-full items-center justify-between',
                    defaultClassNames.nav,
                ),
                button_previous: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'h-8 w-8 p-0 text-foreground select-none aria-disabled:opacity-40',
                    defaultClassNames.button_previous,
                ),
                button_next: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'h-8 w-8 p-0 text-foreground select-none aria-disabled:opacity-40',
                    defaultClassNames.button_next,
                ),
                month_caption: cn(
                    'flex h-8 w-full items-center justify-center px-8 text-sm font-semibold',
                    defaultClassNames.month_caption,
                ),
                dropdowns: cn(
                    'flex h-8 w-full items-center justify-center gap-1.5 text-sm font-medium',
                    defaultClassNames.dropdowns,
                ),
                dropdown_root: cn(
                    'relative rounded-md border border-border shadow-sm focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50',
                    defaultClassNames.dropdown_root,
                ),
                dropdown: cn('absolute inset-0 opacity-0', defaultClassNames.dropdown),
                caption_label: cn(
                    'select-none font-semibold text-foreground',
                    captionLayout === 'label'
                        ? 'text-sm'
                        : 'flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:text-muted-foreground',
                    defaultClassNames.caption_label,
                ),
                month_grid: cn('w-full border-collapse', defaultClassNames.month_grid),
                weekdays: cn('flex', defaultClassNames.weekdays),
                weekday: cn(
                    'w-10 flex-none select-none text-center text-xs font-medium uppercase tracking-wide text-muted-foreground',
                    defaultClassNames.weekday,
                ),
                week: cn('mt-1 flex w-full', defaultClassNames.week),
                week_number_header: cn('w-10 select-none', defaultClassNames.week_number_header),
                week_number: cn(
                    'select-none text-xs text-muted-foreground',
                    defaultClassNames.week_number,
                ),
                day: cn(
                    'group/day relative h-10 w-10 p-0 text-center',
                    defaultClassNames.day,
                ),
                range_start: cn(
                    'rounded-l-full bg-primary/10 [&.rdp-range_end]:rounded-none [&.rdp-range_end]:bg-transparent',
                    defaultClassNames.range_start,
                ),
                range_middle: cn('bg-primary/10', defaultClassNames.range_middle),
                range_end: cn(
                    'rounded-r-full bg-primary/10 [&.rdp-range_start]:rounded-none [&.rdp-range_start]:bg-transparent',
                    defaultClassNames.range_end,
                ),
                today: cn('text-foreground', defaultClassNames.today),
                outside: cn('text-muted-foreground', defaultClassNames.outside),
                disabled: cn('text-muted-foreground/35', defaultClassNames.disabled),
                hidden: cn('invisible', defaultClassNames.hidden),
                ...classNames,
            }}
            components={{
                Root: ({ className: rootClassName, rootRef, ...rootProps }) => (
                    <div
                        data-slot="calendar"
                        ref={rootRef}
                        className={cn(rootClassName)}
                        {...rootProps}
                    />
                ),
                Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) => {
                    if (orientation === 'left') {
                        return (
                            <ChevronLeft
                                className={cn('h-4 w-4', chevronClassName)}
                                {...chevronProps}
                            />
                        );
                    }

                    if (orientation === 'right') {
                        return (
                            <ChevronRight
                                className={cn('h-4 w-4', chevronClassName)}
                                {...chevronProps}
                            />
                        );
                    }

                    return (
                        <ChevronDown className={cn('h-4 w-4', chevronClassName)} {...chevronProps} />
                    );
                },
                DayButton: CalendarDayButton,
                WeekNumber: ({ children, ...weekNumberProps }) => (
                    <td {...weekNumberProps}>
                        <div className="flex h-10 w-10 items-center justify-center text-center">
                            {children}
                        </div>
                    </td>
                ),
                ...components,
            }}
            {...props}
        />
    );
}

Calendar.displayName = 'Calendar';

export { Calendar, CalendarDayButton };
